/* eslint-disable @typescript-eslint/no-throw-literal */
import React from 'react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { redirect } from 'react-router-dom';

import { rapidstorApi } from '@/core/api/rapidstor';
import Logger from '@/core/utils/logging';
import { PERSONA_LOCK_COOKIE } from '@/core/utils/rapidstor';

import store from '@/store';

import Spinner from '@/core/components/elements/spinner';

/**
 * Collects query params from both the regular query string and the hash query string.
 */
export const getQueryParams = (): URLSearchParams => {
    const queryParams = new URLSearchParams(window.location.search);
    const hashQuery = window.location.hash.split('?')[1];
    if (hashQuery) {
        new URLSearchParams(hashQuery).forEach((value, key) => {
            if (!queryParams.has(key)) {
                queryParams.set(key, value);
            }
        });
    }
    return queryParams;
};

/**
 * Resolves persona from query parameters by matching against persona tag parameters from the API.
 * Sets a cookie if a persona is matched so that route construction and components can detect it.
 */
export const resolvePersonaFromQueryParams = async (): Promise<boolean> => {
    const personaCookie = document.cookie.split('; ').find((c) => c.startsWith('persona='));
    if (personaCookie) return false;

    const { locationCode, corpCode } = window.rapidstor_settings.rapidstor_options;
    if (!locationCode || !corpCode) return false;

    const queryParams = getQueryParams();

    // If personaId is directly in the query params, set the cookie immediately.
    const personaId = queryParams.get('personaId');
    if (personaId) {
        document.cookie = `persona=${personaId}; path=/; max-age=86400`;
        document.cookie = `${PERSONA_LOCK_COOKIE}=1; path=/; max-age=86400`;
        window.rapidstor_settings.rapidstor_options.persona = personaId;
        return true;
    }

    // No query params to match against — skip the API call.
    if ([...queryParams].length === 0) return false;

    // Fetch personas and try to match query params against persona tag parameters.
    const promise = store.dispatch(
        rapidstorApi.endpoints.getPersonas.initiate({ locationCode, corpCode }),
    );

    try {
        const personas = await promise.unwrap();
        const matched = personas.find((p) => p.parameters?.some(
            (param) => queryParams.get(param.name) === param.value,
        ));
        if (matched?._id) {
            document.cookie = `persona=${matched._id}; path=/; max-age=86400`;
            document.cookie = `${PERSONA_LOCK_COOKIE}=1; path=/; max-age=86400`;
            window.rapidstor_settings.rapidstor_options.persona = matched._id;
            return true;
        }
    } catch (err) {
        Logger.warn(`[locationLoader] Failed to fetch personas for tag matching: ${err}`);
    } finally {
        promise.unsubscribe();
    }

    return false;
};

// Handles layout adjustments when hash changes for specific pages.
export const handlePageChange = () => {
    const rsContainer = document.getElementById('rapidstor-v2-frontend');
    const path = window.location.hash.search(
        /(booking|reservation-with-fee|move-in|tests|)/,
    );
    if (rsContainer) {
        rsContainer.style.display = 'none';
        if (path !== -1) {
            rsContainer.style.minHeight = '100vh';
            rsContainer.style.height = '';
        } else {
            rsContainer.style.height = '100vh';
            rsContainer.style.minHeight = '';
        }
    }
};

/**
 * Renders an element to an HTML string on the client. react-dom/server would do
 * this too, but it adds ~100 KB gzipped to the bundle for one spinner.
 */
const renderToHtml = (element: React.ReactElement): string => {
    const container = document.createElement('div');
    const root = createRoot(container);
    flushSync(() => root.render(element));
    const html = container.innerHTML;
    root.unmount();
    return html;
};

/**
 * Loader responsible for initial location fetch + spinner management.
 * Separated so it can be reused across route groups.
 */
export const locationLoader = async () => {
    if (
        !window.rapidstor_settings.app_config
    || !window.rapidstor_settings.rapidstor_options
    ) {
        Logger.error(`App config not found: ${window.location}`);
        throw new Response('Error', {
            status: 500,
            statusText: 'App config not found',
        });
    }

    const rsContainer = document.getElementById('rapidstor-v2-frontend');
    handlePageChange();

    const spinner = document.getElementById('spinner');
    if (!spinner) {
        rsContainer?.insertAdjacentHTML(
            'beforebegin',
            renderToHtml(<Spinner loader size="100" useDataAttribute />),
        );
    }

    const promise = store.dispatch(
        rapidstorApi.endpoints.getLocationByCode.initiate({
            locationCode: window.rapidstor_settings.rapidstor_options.locationCode,
        }),
    );

    try {
        const result = await promise.unwrap();

        if (!window.rapidstor_settings.rapidstor_options.locationCode) {
            Logger.warn('[404] Token or LocationCode missing');
            throw new Response('', { status: 404, statusText: 'Not Found' });
        }

        window.rapidstor_settings.rapidstor_options.locationToken = result?.token;
        window.rapidstor_settings.rapidstor_options.location = result;

        if (!result) {
            Logger.warn('[404] Token or LocationCode missing', undefined, [
                {
                    name: 'locationCode',
                    value: window.rapidstor_settings.app_config.journey.locationCode,
                },
                {
                    name: 'corpCode',
                    value: window.rapidstor_settings.app_config.journey.corpCode,
                },
            ]);
            throw new Response('', {
                status: 404,
                statusText: 'The locationCode supplied was not found or not enabled.',
            });
        }

        if (!result.rapidstorEnabled) {
            Logger.warn('[404] RapidStor not enabled', result, []);
            throw new Response('', {
                status: 404,
                statusText: 'The locationCode supplied was not found or not enabled.',
            });
        }

        // Resolve persona from query params before components mount.
        // If a persona was just matched and the routes were built before
        // the cookie existed, the Index screen is still at '/' — redirect
        // to unit-listing so the user skips it.
        const personaJustResolved = await resolvePersonaFromQueryParams();
        if (personaJustResolved) {
            const hashPath = window.location.hash.replace(/^#/, '').split('?')[0];
            if (hashPath === '/' || hashPath === '') {
                return redirect('/unit-listing');
            }
        }

        if (rsContainer) {
            if (spinner) {
                spinner.style.display = 'none';
            }
            rsContainer.style.display = 'revert';
        }

        return result;
    } finally {
        promise.unsubscribe();
    }
};
